/**
 * The API client. Everything goes through the BFF at `/api`, same-origin, cookie-auth.
 */
import type {
  Citation,
  CorpusFacets,
  Notebook,
  ChunkText,
  DocumentChunkPage,
  Collection,
  Conversation,
  ConversationDetail,
  CorpusDocument,
  CorpusPage,
  DocumentStatus,
  IngestionJob,
  QueryRequest,
  QueryResponse,
  QueryTrendReport,
  SessionUser,
  StreamFrame,
} from "./api-types.ts";
import type { V2AnnotationsFrame } from "./queryV2.ts";

/**
 * The `/query/v2/stream` frames: v1's, minus v1's `faithfulness`, plus `annotations`
 * (sent before the text); `message.start` names the contract and `citation` frames
 * carry the key a claim cites them by.
 */
export type V2StreamFrame =
  | Exclude<StreamFrame, { event: "faithfulness" | "message.start" | "citation" }>
  | {
      event: "message.start";
      data: { conversation_id: string; message_id: string; schema?: string };
    }
  | { event: "citation"; data: Citation & { citation_key?: string } }
  | { event: "annotations"; data: V2AnnotationsFrame };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields?: string[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** A 404 from the API. On a notebook it means unknown OR another user's: the API answers
 *  both the same so a notebook's existence never leaks. */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * FastAPI answers with TWO shapes and a client that assumes one will one day render
 * `[object Object]` at the user. `{"detail": "..."}` for our own raised errors;
 * `{"detail": [{loc, msg, type}, ...]}` for a 422 from pydantic, which is what an
 * unknown field produces because every request model sets `extra="forbid"`.
 */
async function readError(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return new ApiError(response.status, `HTTP ${response.status}`);
  }

  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return new ApiError(response.status, detail);

  if (Array.isArray(detail)) {
    const fields = detail.map((item) => {
      const loc = (item as { loc?: unknown[] }).loc ?? [];
      return loc.filter((part) => part !== "body").join(".");
    });
    const messages = detail.map((item) => String((item as { msg?: unknown }).msg ?? "tidak valid"));
    return new ApiError(response.status, messages.join("; "), fields);
  }

  return new ApiError(response.status, `HTTP ${response.status}`);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: { accept: "application/json", ...(init?.headers ?? {}) },
    credentials: "same-origin",
  });
  if (!response.ok) throw await readError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function json(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

// ------------------------------------------------------------------- auth --
export const auth = {
  me: () => request<{ user: SessionUser }>("/auth/me").then((r) => r.user),
  login: (username: string, password: string) =>
    request<{ user: SessionUser }>("/auth/login", json({ username, password })).then((r) => r.user),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
};

// -------------------------------------------------------------------- rag --
export const api = {
  query: (body: QueryRequest) => request<QueryResponse>("/rag/query", json(body)),

  conversations: (userId: string) =>
    request<Conversation[]>(`/rag/conversations?user_id=${encodeURIComponent(userId)}`),

  conversation: (id: string, userId: string) =>
    request<ConversationDetail>(
      `/rag/conversations/${encodeURIComponent(id)}?user_id=${encodeURIComponent(userId)}`,
    ),

  createConversation: (userId: string, title?: string) =>
    request<Conversation>("/rag/conversations", json({ user_id: userId, title })),

  collections: () => request<Collection[]>("/rag/collections"),

  createCollection: (body: {
    name: string;
    description?: string;
    owning_org_unit?: string;
    min_position_level?: number;
    confidential?: boolean;
  }) => request<Collection>("/rag/collections", json(body)),

  deleteCollection: (id: string) =>
    request<void>(`/rag/collections/${encodeURIComponent(id)}`, { method: "DELETE" }),

  corpus: (params: {
    limit?: number;
    offset?: number;
    collection_id?: string;
    status?: DocumentStatus;
    search?: string;
    year?: string;
    wilayah?: string;
    /** Named ids — how a notebook reads back the documents it holds. */
    ids?: string[];
  }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === "") continue;
      // Repeated key, not a joined string: FastAPI reads a list query param that way, and
      // a comma-joined value would arrive as one id that matches nothing.
      if (Array.isArray(value)) value.forEach((item) => query.append(key, String(item)));
      else query.set(key, String(value));
    }
    return request<CorpusPage>(`/rag/corpus/documents?${query.toString()}`);
  },

  /** The closed vocabularies the library's dropdowns offer. Access-gated server-side,
   *  and NOT derivable from the page on screen — that is 50 rows of several thousand. */
  // `json()` rather than a bare body: it sets `content-type: application/json`, and
  // without that FastAPI cannot parse the body at all — it answers 422 "Input should be a
  // valid dictionary", which reads like a schema problem and is not one.
  /** Rename a notebook. `null` clears the name. */
  renameNotebook: (notebookId: string, userId: string, title: string | null) =>
    request<Notebook>(
      `/rag/notebooks/${encodeURIComponent(notebookId)}?user_id=${encodeURIComponent(userId)}`,
      { ...json({ title }), method: "PATCH" },
    ),

  createNotebook: (title?: string) =>
    request<Notebook>("/rag/notebooks", json(title ? { title } : {})),

  notebook: (id: string, userId: string) =>
    request<Notebook>(
      `/rag/notebooks/${encodeURIComponent(id)}?user_id=${encodeURIComponent(userId)}`,
    ),

  /** REPLACE, not merge: the body is the whole intended set, so an empty one empties the
   *  notebook. A merge could not express removal. */
  replaceNotebookSources: (id: string, userId: string, body: unknown) =>
    request<Notebook>(
      `/rag/notebooks/${encodeURIComponent(id)}/sources?user_id=${encodeURIComponent(userId)}`,
      { ...json(body), method: "PUT" },
    ),

  corpusFacets: (params: {
    collection_id?: string;
    status?: DocumentStatus;
    search?: string;
    year?: string;
    wilayah?: string;
  } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") query.set(key, String(value));
    }
    return request<CorpusFacets>(`/rag/corpus/facets?${query.toString()}`);
  },

  upload: (form: FormData) =>
    request<IngestionJob>("/rag/documents", { method: "POST", body: form }),

  job: (jobId: string) => request<IngestionJob>(`/rag/ingestion/jobs/${encodeURIComponent(jobId)}`),

  /** The TEXT behind a citation. The stream carries it while an answer is produced;
   *  this is how a citation stays readable in a conversation reopened later. */
  chunk: (chunkId: string) =>
    request<ChunkText>(`/rag/corpus/chunks/${encodeURIComponent(chunkId)}`),

  /** How a document was SPLIT: its indexed passages, in reading order. */
  documentChunks: (documentId: string, limit = 20) =>
    request<DocumentChunkPage>(
      `/rag/corpus/documents/${encodeURIComponent(documentId)}/chunks?limit=${limit}`,
    ),

  /** The ORIGINAL file behind a document. Same-origin through the BFF, so the browser
   *  sends the session cookie and the API key never leaves the server. */
  documentContentUrl: (documentId: string) =>
    `/api/rag/corpus/documents/${encodeURIComponent(documentId)}/content`,

  /** File a document into a collection, or `null` to take it out of one.
   *
   *  A collection is a LABEL, so this changes one field and nothing else: the document
   *  stays exactly as searchable, and who may read it does not move. Before this existed
   *  the label could only be set by the upload that created the document, which made an
   *  upload filed to the wrong place — or to nowhere — permanent. */
  setDocumentCollection: (documentId: string, collectionId: string | null) =>
    request<CorpusDocument>(`/rag/corpus/documents/${encodeURIComponent(documentId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ collection_id: collectionId }),
    }),

  queryTrends: (keywordLimit = 10) =>
    request<QueryTrendReport>(
      `/rag/analytics/query-trends?keyword_limit=${encodeURIComponent(keywordLimit)}`,
    ),
};

// -------------------------------------------------------------- streaming --
/**
 * Consumed with `fetch` + `ReadableStream`, NOT `EventSource`: `EventSource` cannot
 * send a POST body, and `/query/stream` is a POST.
 *
 * Note what this is honest about — the backend runs the entire agent loop, the
 * faithfulness gate, the Policy Guard and persistence BEFORE the first byte. There is
 * no heartbeat. So a caller waits in silence for the length of a whole model run, and
 * the UI must say "waiting", not fake progress.
 */
export async function* streamQuery(
  body: QueryRequest,
  signal?: AbortSignal,
): AsyncGenerator<StreamFrame> {
  for await (const frame of streamFrames("/api/rag/query/stream", body, signal)) {
    yield frame as StreamFrame;
  }
}

/**
 * `/query/v2/stream`: an `annotations` frame with every claim's verdict and offsets,
 * sent before the answer text (ADR-0051). The body is v1's plus `accept_annotations`,
 * the former opt-in: the route now ignores it, but an API image from before 23 Oct 2026
 * answers 400 without it. `offer_artifacts` is left unset because v2 accepts it only as
 * false.
 */
export async function* streamQueryV2(
  body: QueryRequest,
  signal?: AbortSignal,
): AsyncGenerator<V2StreamFrame> {
  const v2Body = { ...body, accept_annotations: true as const };
  for await (const frame of streamFrames("/api/rag/query/v2/stream", v2Body, signal)) {
    yield frame as V2StreamFrame;
  }
}

async function* streamFrames(
  path: string,
  body: QueryRequest & { accept_annotations?: true },
  signal?: AbortSignal,
): AsyncGenerator<{ event: string; data: unknown }> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "text/event-stream" },
    body: JSON.stringify(body),
    credentials: "same-origin",
    signal,
  });

  if (!response.ok) throw await readError(response);
  if (!response.body) throw new ApiError(502, "Aliran jawaban kosong.");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line. A frame split across two network
      // reads is normal, so never parse on a chunk boundary.
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const raw = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const frame = parseFrame(raw);
        if (frame) yield frame;
        boundary = buffer.indexOf("\n\n");
      }
    }
  } finally {
    reader.cancel().catch(() => undefined);
  }
}

function parseFrame(raw: string): { event: string; data: unknown } | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    // `:` comments and unknown fields are ignored, as the spec requires.
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) as unknown };
  } catch {
    // An unparseable frame is not a reason to kill a stream that may still recover.
    return null;
  }
}
